import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiRequest } from '@app/core/backend';
import { ApiClient } from '@eng-ds/api-client';
import {
	NbAuthRefreshableToken,
	NbAuthResult,
	NbAuthStrategyClass,
	NbAuthToken,
	NbPasswordAuthStrategy
} from '@nebular/auth';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
	PasswordAuthStrategyOptions,
	passwordStrategyOptions
} from './password-strategy-options';
import { AppConfig } from '@app/app.config';

@Injectable()
export class PasswordAuthStrategy extends NbPasswordAuthStrategy {
	protected defaultOptions: PasswordAuthStrategyOptions =
		passwordStrategyOptions;

	static setup(
		options: PasswordAuthStrategyOptions
	): [NbAuthStrategyClass, PasswordAuthStrategyOptions] {
		return [PasswordAuthStrategy, options];
	}

	constructor(
		protected http: HttpClient,
		route: ActivatedRoute,
		private apiClient: ApiClient
	) {
		super(http, route);
	}

	authenticate(data?: any): Observable<NbAuthResult> {
		const module = 'login';
		const method = this.getOption(`${module}.method`);
		const url = this.getActionEndpoint(module);

		let headers = new HttpHeaders();
		headers = headers.append(
			'Content-Type',
			'application/x-www-form-urlencoded'
		);
		const body = this.buildPasswordRequestData(
			data.username,
			data.password
		);

		const requireValidToken = this.getOption(`${module}.requireValidToken`);

		return this.http
			.request(method, url, { body, observe: 'response', headers })
			.pipe(
				map(res => {
					if (this.getOption(`${module}.alwaysFail`)) {
						throw this.createFailResponse(data);
					}
					return res;
				}),
				map(res => {
					return new NbAuthResult(
						true,
						res,
						this.getOption(`${module}.redirect.success`),
						[],
						this.getOption('messages.getter')(
							module,
							res,
							this.options
						),
						this.createToken(
							this.getOption('token.getter')(
								module,
								res,
								this.options
							),
							requireValidToken
						)
					);
				}),
				catchError(res => {
					return this.handleResponseError(res, module);
				})
			);
	}

	protected buildPasswordRequestData(username: string, password: string) {
		const params = {
			username: username,
			password: password
		};
		return this.urlEncodeParameters(
			this.cleanParams(this.addCredentialsToParams(params))
		);
	}

	protected urlEncodeParameters(params: any): string {
		return Object.keys(params)
			.map(k => {
				return `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`;
			})
			.join('&');
	}

	protected cleanParams(params: any): any {
		Object.entries(params).forEach(
			([key, val]) => !val && delete params[key]
		);
		return params;
	}

	private addCredentialsToParams(params: any) {
		if (this.getOption('clientId') && this.getOption('clientSecret')) {
			return {
				grant_type: this.getOption('grantType'),
				client_id: this.getOption('clientId'),
				client_secret: this.getOption('clientSecret'),
				...params
			};
		} else {
			throw Error(
				'For request body client authentication method, please provide both clientId & clientSecret.'
			);
		}
	}

	logout = (): Observable<NbAuthResult> => of(new NbAuthResult(true));

	// const module = 'logout';
	// const body = {
	//   UserId: this.userService.user.Username
	// };

	// return this.apiClient.request('HOME_8', body).pipe(
	// switchMap((res: ApiResponse<BEBase>) => {
	// if (res.Header.esito === 1) {
	// return of(new NbAuthResult(true));
	// } else {
	//   // return of(new NbAuthResult(false));
	//   throw this.createFailResponse();
	// }
	// }),
	// catchError((res) => {
	// return of(new NbAuthResult(true));
	// })
	// );

	refreshToken(data?: NbAuthRefreshableToken): Observable<NbAuthResult> {
		const module = 'refreshToken';
		const method = this.getOption(`${module}.method`);
		const url = this.getActionEndpoint(module);

		let headers = new HttpHeaders();
		headers = headers.append(
			'Content-Type',
			'application/x-www-form-urlencoded'
		);
		const requireValidToken = this.getOption(`${module}.requireValidToken`);

		const body = this.buildRefreshRequestData(data);

		return this.http
			.request(method, url, { body, observe: 'response', headers })
			.pipe(
				map(res => {
					if (this.getOption(`${module}.alwaysFail`)) {
						throw this.createFailResponse(data);
					}

					return res;
				}),
				map(res => {
					return new NbAuthResult(
						true,
						res,
						this.getOption(`${module}.redirect.success`),
						[],
						this.getOption('messages.getter')(
							module,
							res,
							this.options
						),
						this.createRefreshedToken(
							res?.body,
							data,
							requireValidToken
						)
					);
				}),
				catchError(res => {
					return this.handleResponseError(res, module);
				})
			);
	}

	protected buildRefreshRequestData(token: NbAuthRefreshableToken): any {
		const params = {
			grant_type: 'refresh_token',
			refresh_token: token.getRefreshToken(),
			client_id: this.getOption('clientId'),
			client_secret: this.getOption('clientSecret')
		};
		return this.urlEncodeParameters(this.cleanParams(params));
	}

	protected createRefreshedToken(
		res,
		existingToken: NbAuthRefreshableToken,
		requireValidToken: boolean
	): NbAuthToken {
		type AuthRefreshToken = NbAuthRefreshableToken & NbAuthToken;

		const refreshedToken: AuthRefreshToken =
			this.createToken<AuthRefreshToken>(res, requireValidToken);
		if (
			!refreshedToken.getRefreshToken() &&
			existingToken.getRefreshToken()
		) {
			refreshedToken.setRefreshToken(existingToken.getRefreshToken());
		}
		return refreshedToken;
	}

	resetPassword(data: any = {}): Observable<NbAuthResult> {
		const module = 'resetPass';
		const method = this.getOption(`${module}.method`);
		const url = this.getActionEndpoint(module);

		const apiRequest = new ApiRequest({
			Body: {
				...data,
				LinkPerConfermaPassword: `${AppConfig.hostname}/${this.getOption(
					`${module}.emailLinkReset`
				)}`
			}
		});

		return this.http
			.request(method, url, { body: apiRequest, observe: 'response' })
			.pipe(
				map(res => {
					if (this.getOption(`${module}.alwaysFail`)) {
						throw this.createFailResponse();
					}
					return res;
				}),
				map(res => {
					return new NbAuthResult(
						true,
						res,
						this.getOption(`${module}.redirect.success`),
						[],
						['RESET_PASSWORD.SUCCESS_MESSAGE']
					);
				}),
				catchError(res => {
					return this.handleResponseError(res, module);
				})
			);
	}

	changePassword() {}
}
